/**
 * Daily-grain data behind each card's sparkline. A separate panel from
 * `api.ts#vitalityPanels`'s `*-range`/`resting-*` panels (those are
 * `grain: 'range'` — one aggregate number, current vs. previous period —
 * while a sparkline needs one point per day). Same 5 metrics (`sleep_duration`, `steps`,
 * `hrv`, `stress`, `heart_rate`) in one `grain: 'day'` panel, bundled into
 * the same batch call rather than a second round trip.
 */

import type { DashboardPanelResult, DashboardPanelSpec } from '@/features/statistics/api';
import { compositeScore, normalize, type CardId } from '@/features/vitality/metrics';
import type { VitalityTarget } from '@/features/vitality/api';

export interface DailyPoint {
  date: string;
  value: number | null;
}

const DAILY_METRICS = ['sleep_duration', 'steps', 'hrv', 'stress', 'heart_rate'] as const;

export function dailySeriesPanel(
  target: VitalityTarget,
  range: { from: string; to: string },
): DashboardPanelSpec {
  return {
    id: 'daily',
    type: 'series',
    target,
    grain: 'day',
    range,
    metrics: [...DAILY_METRICS],
  };
}

function pointsFor(result: DashboardPanelResult | undefined, metricKey: string): DailyPoint[] {
  const series = result?.series?.find((s) => s.metric_key === metricKey);
  if (!series) return [];
  return series.buckets.map((b) => ({ date: b.first_date ?? b.bucket, value: b.value }));
}

/** Resting heart rate's daily point is the bucket's own minimum, same proxy
 * `deriveCards.ts#restingHeartRateFromSeries` uses for the range totals. */
function restingPointsFor(result: DashboardPanelResult | undefined): DailyPoint[] {
  const series = result?.series?.find((s) => s.metric_key === 'heart_rate');
  if (!series) return [];
  return series.buckets.map((b) => ({ date: b.first_date ?? b.bucket, value: b.min ?? null }));
}

function byDate(points: DailyPoint[]): Map<string, number | null> {
  return new Map(points.map((p) => [p.date, p.value]));
}

export function deriveDailySeries(
  daily: DashboardPanelResult | undefined,
): Record<CardId, DailyPoint[]> | null {
  if (!daily || daily.status !== 'ok') return null;

  const sleep = pointsFor(daily, 'sleep_duration');
  const activity = pointsFor(daily, 'steps');
  const hrv = pointsFor(daily, 'hrv');
  const stress = pointsFor(daily, 'stress');
  const restingHeartRate = restingPointsFor(daily);

  const dates = Array.from(
    new Set([...sleep, ...activity, ...hrv, ...stress, ...restingHeartRate].map((p) => p.date)),
  ).sort();

  const sleepByDate = byDate(sleep);
  const activityByDate = byDate(activity);
  const hrvByDate = byDate(hrv);
  const stressByDate = byDate(stress);
  const restingByDate = byDate(restingHeartRate);

  const recovery: DailyPoint[] = [];
  const vitalityScore: DailyPoint[] = [];
  for (const date of dates) {
    const s = sleepByDate.get(date) ?? null;
    const a = activityByDate.get(date) ?? null;
    const h = hrvByDate.get(date) ?? null;
    const st = stressByDate.get(date) ?? null;
    const r = restingByDate.get(date) ?? null;

    recovery.push({
      date,
      value: compositeScore([normalize('sleep', s), normalize('hrv', h), normalize('stress', st)]),
    });
    vitalityScore.push({
      date,
      value: compositeScore([
        normalize('sleep', s),
        normalize('activity', a),
        normalize('hrv', h),
        normalize('stress', st),
        normalize('restingHeartRate', r),
      ]),
    });
  }

  return { sleep, activity, hrv, stress, restingHeartRate, recovery, vitalityScore };
}

/** The weekday (in the given locale) whose average step count is highest —
 * "Dinsdag is de meest actieve dag" in the AI-insight banner. `null` when
 * there's no activity data to rank. */
export function mostActiveWeekday(activity: DailyPoint[], locale: string): string | null {
  const sums = new Map<number, { total: number; count: number }>();
  for (const point of activity) {
    if (point.value === null) continue;
    const day = new Date(`${point.date}T00:00:00Z`).getUTCDay();
    const entry = sums.get(day) ?? { total: 0, count: 0 };
    entry.total += point.value;
    entry.count += 1;
    sums.set(day, entry);
  }
  let best: { day: number; avg: number } | null = null;
  for (const [day, { total, count }] of sums) {
    const avg = total / count;
    if (best === null || avg > best.avg) best = { day, avg };
  }
  if (best === null) return null;
  // A fixed, known-Sunday-first UTC date so `getUTCDay()`'s 0-6 maps back to
  // a real calendar date to format — the year/month don't matter.
  const reference = new Date(Date.UTC(2023, 0, 1 + best.day));
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(reference);
}
